import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { EmptyState, ErrorState, SkeletonStorefront } from "@branding/ui";
import { Button } from "@heroui/react";
import useSWR from "swr";
import { ProductCard } from "../components/ProductCard";
import { searchCatalog } from "../lib/api";
import { useSsrData } from "../lib/ssr-data";
import type { Product } from "../lib/types";
import { ApiClientError } from "../lib/v2";

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q")?.trim() ?? "";
  const ssr = useSsrData();
  const fallback =
    ssr?.kind === "search" && ssr.query === q ? ssr.products : undefined;
  const { data, error, isLoading, mutate } = useSWR<Product[]>(
    q ? `search:${q}` : null,
    () => searchCatalog(q),
    { fallbackData: fallback },
  );
  const products = data ?? [];
  const title = useMemo(
    () => (q ? `Hasil untuk “${q}”` : "Cari di Pasar Kupang"),
    [q],
  );

  if (error && !(error instanceof ApiClientError)) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <ErrorState
          title="Pencarian gagal"
          onRetry={() => {
            void mutate();
          }}
        />
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
      <h1 className="text-3xl font-semibold">{title}</h1>
      {!q ? (
        <p className="mt-3 max-w-prose text-muted">
          Ketik nama produk, toko, atau bahan di kolom cari.
        </p>
      ) : null}
      <div className="mt-8">
        {q && isLoading && !products.length ? (
          <SkeletonStorefront />
        ) : q && products.length === 0 ? (
          <EmptyState
            title="Tidak ada hasil"
            description="Coba kata lain atau jelajahi etalase."
            action={
              <Button variant="primary" onPress={() => window.location.assign("/pasar")}>
                Jelajahi pasar
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
